import {
  Box,
  Flex,
  NumberFormatter,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { FormattedMessage } from "react-intl";
import type { TransferAllocationCardProps } from "../../types";
import { AllocationBarChart } from "../allocationBarChart";
import { isMobileDevice } from "../../utils/useDeviceDetection";
import classes from "./index.module.css";
import { REPLAY_BLOCK_CLASS } from "../../utils/statsig";

const isMobile = isMobileDevice();

const sek = (value: number, prefix = "") => (
  <NumberFormatter
    className={REPLAY_BLOCK_CLASS}
    value={value}
    decimalScale={0}
    thousandSeparator=" "
    suffix=" SEK"
    prefix={prefix}
  />
);

export function TransferAllocationCard({ data }: TransferAllocationCardProps) {
  const items = (data?.transferAllocation?.assetAllocation ?? []).map(
    (asset) => ({
      name: asset.name,
      share: asset.percentage,
    }),
  );

  if (items.length === 0) return null;

  const transfer = data.transferAllocation?.insurancesForTransfer;
  const currentFees = data.adviceSummary?.totalCurrentFees ?? 0;
  const advicedFees = data.adviceSummary?.totalAdvicedFees ?? 0;
  const feeDifference = data.adviceSummary?.feeDifference ?? 0;
  const saving = feeDifference <= 0;

  const currentFeesLabel = (
    <FormattedMessage
      id="adviceSummary.allocationCard.currentFees"
      defaultMessage="Avgifter idag"
    />
  );
  const feesAfterTransferLabel = (
    <FormattedMessage
      id="adviceSummary.allocationCard.feesAfterTransfer"
      defaultMessage="Avgifter efter flytt"
    />
  );
  const savedFees = (
    <Box className={classes.savedFees}>
      <Text c="dimmed" size="sm" mb={4}>
        <FormattedMessage
          id="adviceSummary.allocationCard.savedFees"
          defaultMessage="Sparade avgifter"
        />
      </Text>
      <Text
        fw={700}
        c={
          saving
            ? "var(--wallet-color-sambla-green-shade-700)"
            : "var(--wallet-color-sambla-red-shade-600)"
        }
      >
        {sek(Math.abs(feeDifference), saving ? "" : "-")}
      </Text>
    </Box>
  );

  return (
    <Stack gap="md" className={classes.card} data-testid="transfer-allocation">
      <Stack gap={4}>
        <Title ff="var(--wallet-font-family-sambla-sans)" order={3} size="h4">
          <FormattedMessage
            id="adviceSummary.allocationCard.title"
            defaultMessage="Den del som flyttas kommer att placeras såhär"
          />
        </Title>
        <Text size="sm" c="var(--wallet-color-sambla-group-neutrals-500)">
          För att uppnå din målpension kommer vi att flytta{" "}
          {sek(transfer?.capitalForTransfer ?? 0)} av totalt{" "}
          {sek(transfer?.totalTransferableCapital ?? 0)} som är utsatt för
          marknadsrisk (
          <NumberFormatter
            value={transfer?.precentageOfTotal ?? 0}
            decimalScale={0}
            suffix=" %"
            prefix="ca "
          />
          ). Pengarna kommer att placeras enligt följande:
        </Text>
      </Stack>

      <AllocationBarChart items={items} />

      {isMobile ? (
        <Stack gap="sm" className={classes.fees}>
          <Flex justify="space-between" gap="md">
            <Text c="dimmed" size="sm">
              {currentFeesLabel}
            </Text>
            <Text fw={700} size="sm">
              {sek(currentFees)}
            </Text>
          </Flex>
          <Flex justify="space-between" gap="md">
            <Text c="dimmed" size="sm">
              {feesAfterTransferLabel}
            </Text>
            <Text fw={700} size="sm">
              {sek(advicedFees)}
            </Text>
          </Flex>
          {savedFees}
        </Stack>
      ) : (
        <SimpleGrid cols={3} spacing="md" className={classes.fees}>
          <Box>
            <Text c="dimmed" size="sm" mb={4}>
              {currentFeesLabel}
            </Text>
            <Text fw={700}>{sek(currentFees)}</Text>
          </Box>
          <Box>
            <Text c="dimmed" size="sm" mb={4}>
              {feesAfterTransferLabel}
            </Text>
            <Text fw={700}>{sek(advicedFees)}</Text>
          </Box>
          {savedFees}
        </SimpleGrid>
      )}
    </Stack>
  );
}
